import { useState, useEffect, useCallback, useRef } from 'react'
import { createConsumer } from '@rails/actioncable'
import SignatureCanvas from 'react-signature-canvas'
import { useFlashToast } from '../../../hooks/useFlashToast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronLeft, faLocationDot, faCalendar, faCircleDot,
  faHourglassHalf, faCircleCheck, faCircleXmark, faEyeSlash,
  faCopy, faTag, faFile, faPaperclip, faCommentDots,
  faDownload, faXmark, faFilePdf, faFileImage, faFileVideo, faPaperPlane, faEye,
  faComments, faUser, faFileArrowUp, faFileCircleCheck, faExpand,
  faShieldHalved, faIdCard, faUserCheck, faHandshake, faTriangleExclamation,
  faClockRotateLeft, faArrowRightArrowLeft, faEnvelopeOpen, faLock, faLockOpen,
  faPen, faTrash
} from '@fortawesome/free-solid-svg-icons'
import UserSidebar from '../../../components/user/Sidebar'
import UserTopbar from '../../../components/user/Topbar'

const STATUS_CONFIG = {
  menunggu: { label: 'Menunggu',        icon: faCircleDot,     bg: 'bg-gray-100',   text: 'text-gray-600',    ring: 'ring-gray-200',   bar: 'bg-gray-400',    pct: 15 },
  diproses: { label: 'Sedang Diproses', icon: faHourglassHalf, bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200',  bar: 'bg-amber-400',   pct: 55 },
  selesai:  { label: 'Selesai',         icon: faCircleCheck,   bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200', bar: 'bg-emerald-500', pct: 100 },
  ditolak:  { label: 'Ditolak',         icon: faCircleXmark,   bg: 'bg-red-50',     text: 'text-red-600',     ring: 'ring-red-200',    bar: 'bg-red-400',     pct: 100 },
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function isPreviewable(contentType) {
  if (!contentType) return false
  return (
    contentType.startsWith('image/') ||
    contentType === 'application/pdf' ||
    contentType.startsWith('video/')
  )
}

function fileIcon(contentType) {
  if (!contentType) return faFile
  if (contentType.startsWith('image/')) return faFileImage
  if (contentType === 'application/pdf') return faFilePdf
  if (contentType.startsWith('video/')) return faFileVideo
  return faFile
}

function fileIconColor(contentType) {
  if (!contentType) return 'text-gray-400'
  if (contentType.startsWith('image/')) return 'text-emerald-500'
  if (contentType === 'application/pdf') return 'text-red-500'
  if (contentType.startsWith('video/')) return 'text-blue-500'
  return 'text-gray-400'
}

function PKSModal({ agreedAt, reportId, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3 flex-shrink-0">
          <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <FontAwesomeIcon icon={faShieldHalved} className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-gray-900 text-sm">PKS Kerahasiaan Pelapor</h2>
            <p className="text-[10px] text-gray-400 mt-0.5">Perjanjian Kerahasiaan dan Perlindungan Identitas Pelapor</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto p-6 flex-1">
          <pre className="text-[11.5px] text-gray-700 leading-relaxed whitespace-pre-wrap font-sans">{AGREEMENT_TEXT}</pre>
        </div>
        <div className="px-6 py-3.5 bg-emerald-50 border-t border-emerald-100 flex items-center justify-between gap-2 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <FontAwesomeIcon icon={faShieldHalved} className="w-3 h-3 text-emerald-500 flex-shrink-0" />
            <p className="text-[11px] text-emerald-700 font-medium truncate">Disetujui dan ditandatangani secara digital pada {agreedAt}</p>
          </div>
          {reportId && (
            <a
              href={`/user/reports/${reportId}/pks_pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold rounded-lg transition-colors flex-shrink-0"
            >
              <FontAwesomeIcon icon={faDownload} className="w-3 h-3" />
              Unduh PDF
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function PreviewModal({ file, onClose }) {
  const handleBackdrop = useCallback((e) => {
    if (e.target === e.currentTarget) onClose()
  }, [onClose])

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={handleBackdrop}
    >
      <div className="bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon icon={fileIcon(file.content_type)} className={`w-4 h-4 ${fileIconColor(file.content_type)}`} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{file.name}</p>
              <p className="text-[11px] text-gray-400">{formatBytes(file.size)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            <a
              href={file.url}
              download
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg transition-colors"
            >
              <FontAwesomeIcon icon={faDownload} className="w-3 h-3" />
              Unduh
            </a>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
            >
              <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Preview body */}
        <div className="flex-1 overflow-auto bg-gray-50 min-h-0">
          {file.content_type?.startsWith('image/') ? (
            <div className="flex items-center justify-center w-full h-full p-4">
              <img
                src={file.inline_url}
                alt={file.name}
                className="max-w-full max-h-full object-contain rounded-lg shadow"
              />
            </div>
          ) : file.content_type === 'application/pdf' ? (
            <iframe
              src={file.inline_url}
              title={file.name}
              className="w-full h-full min-h-[60vh]"
              style={{ border: 'none' }}
            />
          ) : file.content_type?.startsWith('video/') ? (
            <div className="flex items-center justify-center w-full h-full p-4">
              <video
                src={file.inline_url}
                controls
                className="max-w-full max-h-full rounded-lg shadow"
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

const AGREEMENT_TEXT = `PERJANJIAN KERAHASIAAN DAN PERLINDUNGAN IDENTITAS PELAPOR
Sistem Pelaporan Whistleblowing (WBS)

Dengan menyetujui permintaan pembukaan identitas ini, Anda menyatakan telah membaca,
memahami, dan menyetujui seluruh ketentuan berikut:

1. TUJUAN PENGGUNAAN DATA
   Data identitas Anda hanya digunakan oleh admin yang berwenang semata-mata untuk
   keperluan verifikasi, klarifikasi, dan penanganan laporan ini. Tidak ada penggunaan
   data di luar keperluan tersebut yang diizinkan.

2. KERAHASIAAN MUTLAK
   Admin berkomitmen penuh menjaga kerahasiaan identitas Anda. Data tidak akan
   diteruskan, dipublikasikan, atau dibagikan kepada pihak mana pun—termasuk
   terlapor—tanpa persetujuan tertulis dari Anda.

3. LARANGAN PENGUNGKAPAN
   Setiap pengungkapan identitas pelapor yang tidak sah merupakan pelanggaran etika
   serius dan dapat dikenai sanksi disiplin berat serta pertanggungjawaban pidana
   sesuai hukum yang berlaku.

4. DASAR HUKUM PERLINDUNGAN
   Anda berhak atas perlindungan penuh sebagaimana dijamin dalam:
   • UU No. 13 Tahun 2006 tentang Perlindungan Saksi dan Korban
   • PP No. 43 Tahun 2018 tentang Tata Cara Pelaksanaan Peran Serta Masyarakat
   • Peraturan perundang-undangan terkait perlindungan whistleblower yang berlaku

5. PENCATATAN DIGITAL
   Persetujuan ini dicatat secara otomatis dalam sistem dengan cap waktu yang tidak
   dapat diubah, dan berfungsi sebagai bukti perjanjian yang sah antara Anda dan admin.

6. SIFAT KEPUTUSAN
   Pemberian persetujuan ini bersifat sukarela dan berlaku khusus untuk laporan ini
   saja. Setelah disetujui, keputusan tidak dapat dicabut kembali. Jika identitas Anda
   merasa terancam setelah membuka identitas, segera hubungi pengelola sistem.`

const CATEGORY_EMOJI = {
  'Korupsi & Gratifikasi': '🏛️', 'Penyalahgunaan Wewenang': '⚖️',
  'Konflik Kepentingan': '🔄', 'Penggelapan Aset Negara': '📦',
  'Suap & Pungli': '💰', 'Pemalsuan Dokumen': '📄',
  'Pelanggaran Kode Etik': '📋', 'Kecurangan Pengadaan': '🛒',
}

const ACTIVITY_CONFIG = {
  submitted:           { icon: faEnvelopeOpen, bg: 'bg-emerald-100', iconColor: 'text-emerald-600', label: 'Laporan Diterima',              badgeBg: 'bg-emerald-50', badgeText: 'text-emerald-700', badgeRing: 'ring-emerald-200' },
  status_changed:      { icon: faArrowRightArrowLeft, bg: 'bg-amber-100', iconColor: 'text-amber-600', label: 'Status Diperbarui',          badgeBg: 'bg-amber-50',   badgeText: 'text-amber-700',   badgeRing: 'ring-amber-200'  },

  identity_requested:  { icon: faIdCard, bg: 'bg-amber-100', iconColor: 'text-amber-600', label: 'Admin Meminta Identitas',               badgeBg: 'bg-amber-50',   badgeText: 'text-amber-700',   badgeRing: 'ring-amber-200'  },
  identity_cancelled:  { icon: faXmark, bg: 'bg-gray-100', iconColor: 'text-gray-500', label: 'Permintaan Identitas Dibatalkan',          badgeBg: 'bg-gray-50',    badgeText: 'text-gray-600',    badgeRing: 'ring-gray-200'   },
  identity_approved:   { icon: faUserCheck, bg: 'bg-emerald-100', iconColor: 'text-emerald-600', label: 'Identitas Dibuka',               badgeBg: 'bg-emerald-50', badgeText: 'text-emerald-700', badgeRing: 'ring-emerald-200' },
  identity_rejected:   { icon: faCircleXmark, bg: 'bg-red-100', iconColor: 'text-red-500', label: 'Permintaan Identitas Ditolak',          badgeBg: 'bg-red-50',     badgeText: 'text-red-600',     badgeRing: 'ring-red-200'    },
  communication_opened: { icon: faLockOpen, bg: 'bg-emerald-100', iconColor: 'text-emerald-600', label: 'Komunikasi Dibuka',               badgeBg: 'bg-emerald-50', badgeText: 'text-emerald-700', badgeRing: 'ring-emerald-200' },
  communication_closed: { icon: faLock, bg: 'bg-gray-100', iconColor: 'text-gray-500', label: 'Komunikasi Ditutup',                       badgeBg: 'bg-gray-50',    badgeText: 'text-gray-600',    badgeRing: 'ring-gray-200'   },
  file_request_opened:  { icon: faFileArrowUp, bg: 'bg-violet-100', iconColor: 'text-violet-600', label: 'Admin Meminta File Bukti',        badgeBg: 'bg-violet-50',  badgeText: 'text-violet-700',  badgeRing: 'ring-violet-200' },
  file_request_closed:  { icon: faFileCircleCheck, bg: 'bg-slate-100', iconColor: 'text-slate-500', label: 'Permintaan File Selesai',      badgeBg: 'bg-slate-50',   badgeText: 'text-slate-600',   badgeRing: 'ring-slate-200'  },
  admin_message_sent:   { icon: faComments, bg: 'bg-blue-100', iconColor: 'text-blue-600', label: 'Admin Mengirim Pesan',                  badgeBg: 'bg-blue-50',    badgeText: 'text-blue-700',    badgeRing: 'ring-blue-200'   },
  user_message_sent:    { icon: faComments, bg: 'bg-cyan-100', iconColor: 'text-cyan-600', label: 'Pelapor Mengirim Pesan',                badgeBg: 'bg-cyan-50',    badgeText: 'text-cyan-700',    badgeRing: 'ring-cyan-200'   },
  user_file_uploaded:   { icon: faFileCircleCheck, bg: 'bg-violet-100', iconColor: 'text-violet-600', label: 'Pelapor Kirim File Bukti',  badgeBg: 'bg-violet-50',  badgeText: 'text-violet-700',  badgeRing: 'ring-violet-200' },
}

const STATUS_LABEL_MAP = { menunggu: 'Menunggu', diproses: 'Sedang Diproses', selesai: 'Selesai', ditolak: 'Ditolak' }
const STATUS_PILL = { menunggu: 'bg-gray-100 text-gray-600', diproses: 'bg-amber-50 text-amber-700', selesai: 'bg-emerald-50 text-emerald-700', ditolak: 'bg-red-50 text-red-600' }

function ActivityTimeline({ activities }) {
  if (!activities?.length) return null
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
        <div className="w-7 h-7 bg-slate-100 rounded-lg flex items-center justify-center">
          <FontAwesomeIcon icon={faClockRotateLeft} className="w-3.5 h-3.5 text-slate-500" />
        </div>
        <div>
          <h2 className="font-semibold text-gray-900 text-sm">Riwayat Aktivitas</h2>
          <p className="text-[10px] text-gray-400">{activities.length} kejadian tercatat</p>
        </div>
      </div>
      <div className="p-5">
        <div className="relative">
          <div className="absolute left-4 top-5 bottom-5 w-0.5 bg-gradient-to-b from-gray-200 via-gray-200 to-transparent" />
          <div className="space-y-0">
            {activities.map((act, idx) => {
              const cfg = ACTIVITY_CONFIG[act.action_type] || ACTIVITY_CONFIG.status_changed
              const isLast = idx === activities.length - 1
              const isIdentity = act.action_type.startsWith('identity_')
              return (
                <div key={act.id} className={`relative flex gap-4 ${isLast ? '' : 'pb-6'}`}>
                  <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${cfg.bg}`}>
                    <FontAwesomeIcon icon={cfg.icon} className={`w-3.5 h-3.5 ${cfg.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-start justify-between gap-2 flex-wrap mb-1.5">
                      <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full ring-1 ${cfg.badgeBg} ${cfg.badgeText} ${cfg.badgeRing}`}>
                        {cfg.label}
                      </span>
                      <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">{act.created_at}</span>
                    </div>
                    {!isIdentity && (act.old_status || act.new_status) && act.action_type !== 'submitted' && (
                      <div className="flex items-center gap-1.5 mb-2">
                        {act.old_status && (
                          <span className={`inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full ${STATUS_PILL[act.old_status] || 'bg-gray-100 text-gray-500'}`}>
                            {STATUS_LABEL_MAP[act.old_status] || act.old_status}
                          </span>
                        )}
                        {act.old_status && act.new_status && act.old_status !== act.new_status && (
                          <FontAwesomeIcon icon={faArrowRightArrowLeft} className="w-2.5 h-2.5 text-gray-300" />
                        )}
                        {act.new_status && act.new_status !== act.old_status && (
                          <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full ring-1 ${STATUS_PILL[act.new_status] || 'bg-gray-100 text-gray-500'} ring-current/20`}>
                            {STATUS_LABEL_MAP[act.new_status] || act.new_status}
                          </span>
                        )}
                      </div>
                    )}
                    {act.action_type === 'submitted' && (
                      <p className="text-xs text-gray-500">Laporan berhasil masuk ke sistem WBS dan menunggu verifikasi tim Inspektorat.</p>
                    )}
                    {act.note && act.action_type !== 'submitted' && !isIdentity && (
                      <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5 mt-1">
                        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Tanggapan</p>
                        <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line line-clamp-4">{act.note}</p>
                      </div>
                    )}
                    {isIdentity && act.note && (
                      <p className="text-xs text-gray-500 leading-relaxed">{act.note}</p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function UserReportsShow({ current_user, report }) {
  useFlashToast()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [showPKS, setShowPKS] = useState(false)
  const [previewFile, setPreviewFile] = useState(null)
  const [comments, setComments] = useState(report.comments || [])
  const [communicationOpen, setCommunicationOpen] = useState(report.communication_open ?? false)
  const [fileRequestOpen, setFileRequestOpen] = useState(report.file_request_open ?? false)
  const [evidenceFilesCount, setEvidenceFilesCount] = useState(report.evidence_files_count ?? 0)
  const [selectedFiles, setSelectedFiles] = useState([])
  const fileInputRef = useRef(null)
  const [commentBody, setCommentBody] = useState('')
  const [sendingComment, setSendingComment] = useState(false)
  // null = use server prop; true/false = WS/action override
  const [identityWS, setIdentityWS] = useState({ requested: null, approved: null, agreedAt: null })
  const [identityLoading, setIdentityLoading] = useState(false)
  const sigPadRef = useRef(null)
  const [sigEmpty, setSigEmpty] = useState(true)
  const [sigError, setSigError] = useState(null)
  const [activities, setActivities] = useState(report.activities || [])
  const identitySectionRef = useRef(null)

  // Reset WS overrides when navigating to a different report
  useEffect(() => { setIdentityWS({ requested: null, approved: null, agreedAt: null }) }, [report.id])

  // Derived: WS override takes priority; falls back to server prop on every page load
  const identityRequested = identityWS.requested !== null ? identityWS.requested : (report.identity_reveal_requested ?? false)
  const identityApproved  = identityWS.approved  !== null ? identityWS.approved  : (report.identity_reveal_approved  ?? false)
  const identityAgreedAt  = identityWS.agreedAt  ?? report.identity_reveal_agreed_at ?? null
  const commentsEndRef = useRef(null)
  const cableRef = useRef(null)

  useEffect(() => {
    const consumer = createConsumer('/cable')
    cableRef.current = consumer.subscriptions.create(
      { channel: 'ReportCommentsChannel', report_id: report.id },
      {
        received(data) {
          if (data.type === 'communication_toggle') {
            setCommunicationOpen(data.communication_open)
            if (data.activity) setActivities(prev => [...prev, data.activity])
            return
          }
          if (data.type === 'file_request_toggle') {
            setFileRequestOpen(data.file_request_open)
            setEvidenceFilesCount(data.evidence_files_count)
            if (data.activity) setActivities(prev => [...prev, data.activity])
            return
          }
          if (data.type === 'identity_reveal_request') {
            setIdentityWS(prev => ({ ...prev, requested: true }))
            if (data.activity) setActivities(prev => [...prev, data.activity])
            setTimeout(() => identitySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100)
            return
          }
          if (data.type === 'identity_reveal_cancelled') {
            setIdentityWS(prev => ({ ...prev, requested: false }))
            if (data.activity) setActivities(prev => [...prev, data.activity])
            return
          }
          if (data.type === 'identity_reveal_approved') {
            setIdentityWS(prev => ({ ...prev, approved: true, agreedAt: data.identity_reveal_agreed_at }))
            if (data.activity) setActivities(prev => [...prev, data.activity])
            return
          }
          if (data.type === 'identity_reveal_rejected') {
            setIdentityWS(prev => ({ ...prev, requested: false }))
            if (data.activity) setActivities(prev => [...prev, data.activity])
            return
          }
          if (data.evidence_files_count !== undefined) {
            setEvidenceFilesCount(data.evidence_files_count)
            setFileRequestOpen(data.file_request_open)
          }
          if (data.activity) setActivities(prev => [...prev, data.activity])
          setComments(prev => {
            if (prev.some(c => c.id === data.id)) return prev
            return [...prev, data]
          })
        }
      }
    )
    return () => {
      cableRef.current?.unsubscribe()
      consumer.disconnect()
    }
  }, [report.id])

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [comments])

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files)
    const remaining = 5 - evidenceFilesCount
    const valid = files.filter(f => f.size <= 5 * 1024 * 1024)
    const tooLarge = files.filter(f => f.size > 5 * 1024 * 1024)
    const capped = valid.slice(0, remaining)
    if (tooLarge.length > 0) alert(`${tooLarge.length} file melebihi batas 5MB dan tidak disertakan.`)
    if (valid.length > remaining) alert(`Hanya ${remaining} slot tersisa. ${valid.length - remaining} file tidak disertakan.`)
    setSelectedFiles(capped)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const sendComment = async (e) => {
    e.preventDefault()
    const body = commentBody.trim()
    if (!body && selectedFiles.length === 0) return
    if (sendingComment) return
    setSendingComment(true)
    try {
      if (selectedFiles.length > 0) {
        let anyFailed = false
        for (let i = 0; i < selectedFiles.length; i++) {
          const fd = new FormData()
          if (i === 0 && body) fd.append('body', body)
          fd.append('attachment', selectedFiles[i])
          const res = await fetch(`/user/reports/${report.id}/comments`, {
            method: 'POST',
            headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
            body: fd,
          })
          if (!res.ok) {
            const data = await res.json()
            alert(data.errors?.[0] || 'Gagal mengirim file')
            anyFailed = true
            break
          }
        }
        if (!anyFailed) {
          setCommentBody('')
          setSelectedFiles([])
          if (fileInputRef.current) fileInputRef.current.value = ''
        }
      } else {
        const res = await fetch(`/user/reports/${report.id}/comments`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          },
          body: JSON.stringify({ body }),
        })
        if (res.ok) setCommentBody('')
      }
    } finally {
      setSendingComment(false)
    }
  }

  const approveIdentity = async () => {
    if (sigPadRef.current && sigPadRef.current.isEmpty()) {
      setSigError('Harap tanda tangani dokumen terlebih dahulu.')
      return
    }
    setSigError(null)
    setIdentityLoading(true)
    try {
      const signatureData = sigPadRef.current ? sigPadRef.current.toDataURL('image/png') : null
      const res = await fetch(`/user/reports/${report.id}/approve_identity`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({ signature: signatureData }),
      })
      if (res.ok) {
        setIdentityWS(prev => ({
          ...prev,
          approved: true,
          agreedAt: new Date().toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        }))
      }
    } finally {
      setIdentityLoading(false)
    }
  }

  const rejectIdentity = async () => {
    setIdentityLoading(true)
    try {
      const res = await fetch(`/user/reports/${report.id}/reject_identity`, {
        method: 'PATCH',
        headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
      })
      if (res.ok) {
        setIdentityWS(prev => ({ ...prev, requested: false }))
      }
    } finally {
      setIdentityLoading(false)
    }
  }

  const openFile = (att) => {
    if (isPreviewable(att.content_type)) {
      setPreviewFile(att)
    } else {
      const a = document.createElement('a')
      a.href = att.url
      a.download = att.name
      a.click()
    }
  }

  const cfg = STATUS_CONFIG[report.status] || STATUS_CONFIG.menunggu

  const copyTracking = () => {
    navigator.clipboard.writeText(report.tracking_code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      {previewFile && <PreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />}
      {showPKS && <PKSModal agreedAt={identityAgreedAt} reportId={report.id} onClose={() => setShowPKS(false)} />}
      <UserSidebar current_user={current_user} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <UserTopbar current_user={current_user} onMenuToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">

          <div className="flex items-center gap-3 mb-6">
            <a href="/user/reports" className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 text-gray-500 transition-colors">
              <FontAwesomeIcon icon={faChevronLeft} className="w-3.5 h-3.5" />
            </a>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Detail Laporan</h1>
              <p className="text-sm text-gray-500 mt-0.5">Pantau status dan perkembangan laporan Anda</p>
            </div>
          </div>

          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-7 gap-5 items-start">
            <div className="lg:col-span-4 space-y-4">

            {/* Tracking + Status card */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className={`h-1.5 ${cfg.bar}`} />
              <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">Kode Pelacakan</p>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold font-mono text-gray-900 tracking-wider">{report.tracking_code}</span>
                    <button onClick={copyTracking}
                      className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-green-600 transition-colors px-2 py-1 rounded-md hover:bg-green-50">
                      <FontAwesomeIcon icon={faCopy} className="w-3 h-3" />
                      {copied ? 'Tersalin!' : 'Salin'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Dilaporkan pada {report.created_at}</p>
                </div>
                <div className="flex flex-col items-start sm:items-end gap-2">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ring-1 ${cfg.bg} ${cfg.text} ${cfg.ring}`}>
                    <FontAwesomeIcon icon={cfg.icon} className="w-3.5 h-3.5" />
                    {cfg.label}
                  </span>
                  {report.anonymous && (
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <FontAwesomeIcon icon={faEyeSlash} className="w-3 h-3" /> Dikirim secara anonim
                    </span>
                  )}
                </div>
              </div>

              {/* Identity notification banner – compact, scroll-to action */}
              {report.anonymous && identityRequested && !identityApproved && (
                <div className="mx-5 mb-4">
                  <button
                    onClick={() => identitySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className="w-full flex items-center gap-3 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-xl px-4 py-3 transition-colors text-left"
                  >
                    <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon icon={faIdCard} className="w-4 h-4 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-amber-800">Admin Meminta Identitas Anda</p>
                      <p className="text-[11px] text-amber-600 mt-0.5">Klik untuk melihat perjanjian dan merespons permintaan</p>
                    </div>
                    <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse flex-shrink-0" />
                  </button>
                </div>
              )}
              {report.anonymous && identityApproved && (
                <div className="mx-5 mb-4">
                  <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon icon={faShieldHalved} className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-emerald-800">Identitas Telah Dibuka</p>
                      <p className="text-[11px] text-emerald-600 mt-0.5">Perjanjian kerahasiaan tersimpan · {identityAgreedAt}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Progress bar */}
              <div className="px-5 pb-5">
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full ${cfg.bar} rounded-full transition-all duration-700`} style={{ width: `${cfg.pct}%` }} />
                </div>
                <div className="flex justify-between text-[10px] text-gray-400 mt-1.5">
                  <span>Diterima</span><span>Verifikasi</span><span>Diproses</span><span>Selesai</span>
                </div>
              </div>
            </div>

            {/* Report detail card */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                <span className="text-2xl">{CATEGORY_EMOJI[report.category] || '📋'}</span>
                <div>
                  <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-md">{report.category}</span>
                  <h2 className="text-base font-bold text-gray-900 mt-1">{report.title}</h2>
                </div>
              </div>
              <div className="p-6 space-y-5">
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2">Uraian Pelanggaran</p>
                  <div
                    className="text-sm text-gray-700 leading-relaxed [&_strong]:font-semibold [&_em]:italic [&_u]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:text-gray-500 [&_h2]:text-base [&_h2]:font-bold [&_h3]:text-sm [&_h3]:font-bold"
                    dangerouslySetInnerHTML={{ __html: report.description }}
                  />
                </div>

                {(report.location || report.incident_date) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                    {report.location && (
                      <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">Lokasi Kejadian</p>
                        <div className="flex items-center gap-1.5 text-sm text-gray-700">
                          <FontAwesomeIcon icon={faLocationDot} className="w-3.5 h-3.5 text-gray-400" />
                          {report.location}
                        </div>
                      </div>
                    )}
                    {report.incident_date && (
                      <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">Tanggal Kejadian</p>
                        <div className="flex items-center gap-1.5 text-sm text-gray-700">
                          <FontAwesomeIcon icon={faCalendar} className="w-3.5 h-3.5 text-gray-400" />
                          {report.incident_date}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {report.attachments?.length > 0 && (
                  <div className="pt-4 border-t border-gray-100">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-3">
                      <FontAwesomeIcon icon={faPaperclip} className="mr-1" />
                      Bukti Dukung ({report.attachments.length} file)
                    </p>
                    <ul className="space-y-2">
                      {report.attachments.map((att, idx) => (
                        <li key={idx}>
                          <button
                            onClick={() => openFile(att)}
                            className="w-full flex items-center gap-3 bg-gray-50 hover:bg-gray-100 border border-gray-100 rounded-lg px-3 py-2.5 transition-colors group text-left"
                          >
                            <div className="w-8 h-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                              <FontAwesomeIcon icon={fileIcon(att.content_type)} className={`w-3.5 h-3.5 ${fileIconColor(att.content_type)}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-700 group-hover:text-green-700 truncate">{att.name}</p>
                              <p className="text-[11px] text-gray-400">{formatBytes(att.size)}</p>
                            </div>
                            <span className="text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity text-green-600">
                              {isPreviewable(att.content_type) ? 'Lihat' : 'Unduh'}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* ===== Inline identity request card ===== */}
            {report.anonymous && (identityRequested || identityApproved) && (
              <div ref={identitySectionRef} className="scroll-mt-4">
                {identityApproved ? (
                  /* ── Approved state ── */
                  <div className="bg-white rounded-xl border border-emerald-200 overflow-hidden">
                    <div className="h-1 bg-emerald-500" />
                    <div className="px-6 py-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <FontAwesomeIcon icon={faShieldHalved} className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-emerald-800">Perjanjian Kerahasiaan Tersimpan</h3>
                        <p className="text-xs text-emerald-600 mt-0.5">Anda menyetujui pembukaan identitas pada {identityAgreedAt}</p>
                      </div>
                    </div>
                    <div className="px-6 pb-5 space-y-3">
                      <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                        <p className="text-[11.5px] text-emerald-800 leading-relaxed">
                          Data identitas Anda telah dibagikan kepada admin yang berwenang semata-mata untuk keperluan verifikasi laporan ini.
                          Perjanjian kerahasiaan yang Anda setujui telah dicatat secara digital dan tidak dapat diubah, serta berlaku sebagai bukti
                          komitmen admin untuk menjaga kerahasiaan identitas Anda sepenuhnya.
                        </p>
                      </div>
                      <button
                        onClick={() => setShowPKS(true)}
                        className="w-full flex items-center justify-center gap-2 py-2 border border-emerald-200 bg-white hover:bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-xl transition-colors"
                      >
                        <FontAwesomeIcon icon={faEye} className="w-3 h-3" />
                        Lihat Perjanjian Kerahasiaan
                      </button>
                      <a
                        href={`/user/reports/${report.id}/pks_pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full flex items-center justify-center gap-2 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl transition-colors"
                      >
                        <FontAwesomeIcon icon={faDownload} className="w-3 h-3" />
                        Unduh PKS PDF
                      </a>
                    </div>
                  </div>
                ) : (
                  /* ── Pending/request state ── */
                  <div className="bg-white rounded-xl border border-amber-200 overflow-hidden">
                    <div className="h-1 bg-amber-400" />

                    {/* Header */}
                    <div className="px-6 py-4 border-b border-amber-100 flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <FontAwesomeIcon icon={faIdCard} className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-bold text-gray-900">Permintaan Buka Identitas</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Admin memerlukan identitas Anda untuk keperluan verifikasi laporan ini
                          {report.identity_reveal_requested_at && (
                            <span className="ml-1 text-amber-600 font-medium">· diminta sejak {report.identity_reveal_requested_at}</span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 text-[11px] text-amber-600 font-medium">
                        <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                        Menunggu respons
                      </div>
                    </div>

                    {/* Warning notice */}
                    <div className="px-6 pt-5">
                      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                        <FontAwesomeIcon icon={faTriangleExclamation} className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-amber-800 leading-relaxed space-y-1">
                          <p>Admin yang menangani laporan Anda membutuhkan identitas Anda untuk keperluan verifikasi dan klarifikasi.</p>
                          <p className="font-semibold">Keputusan Anda bersifat sukarela dan tidak memengaruhi proses penanganan laporan.</p>
                        </div>
                      </div>
                    </div>

                    {/* Full agreement text */}
                    <div className="px-6 pt-4">
                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-4">
                          <FontAwesomeIcon icon={faHandshake} className="w-4 h-4 text-gray-500" />
                          <h4 className="text-xs font-bold text-gray-600 uppercase tracking-widest">Perjanjian Kerahasiaan &amp; Dasar Hukum</h4>
                        </div>
                        <pre className="text-[11.5px] text-gray-700 leading-relaxed whitespace-pre-wrap font-sans">{AGREEMENT_TEXT}</pre>
                      </div>
                    </div>

                    {/* Signature pad */}
                    <div className="px-6 pt-4">
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
                          <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faPen} className="w-3 h-3 text-gray-500" />
                            <span className="text-xs font-semibold text-gray-600">Tanda Tangan Digital</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => { sigPadRef.current?.clear(); setSigEmpty(true); setSigError(null) }}
                            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-500 transition-colors px-2 py-1 rounded hover:bg-red-50"
                          >
                            <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
                            Bersihkan
                          </button>
                        </div>
                        <div className="bg-white">
                          <SignatureCanvas
                            ref={sigPadRef}
                            penColor="#1d4ed8"
                            canvasProps={{
                              width: 475,
                              height: 120,
                              className: 'w-full',
                              style: { display: 'block', touchAction: 'none' }
                            }}
                            onEnd={() => setSigEmpty(false)}
                          />
                        </div>
                        <p className="text-[11px] text-gray-400 px-4 py-2 bg-gray-50 border-t border-gray-100">
                          Gambar tanda tangan Anda di area putih di atas menggunakan mouse atau layar sentuh
                        </p>
                      </div>
                      {sigError && (
                        <p className="text-[11px] text-red-500 mt-1.5 flex items-center gap-1">
                          <FontAwesomeIcon icon={faTriangleExclamation} className="w-3 h-3" />
                          {sigError}
                        </p>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="px-6 py-5 flex gap-3">
                      <button
                        type="button"
                        onClick={rejectIdentity}
                        disabled={identityLoading}
                        className="flex-1 py-2.5 px-4 border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 text-sm font-semibold rounded-xl transition-colors disabled:opacity-40"
                      >
                        Tolak Permintaan
                      </button>
                      <button
                        type="button"
                        onClick={approveIdentity}
                        disabled={identityLoading}
                        className="flex-1 py-2.5 px-4 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
                      >
                        <FontAwesomeIcon icon={faUserCheck} className="w-3.5 h-3.5" />
                        {identityLoading ? 'Memproses...' : 'Setuju & Buka Identitas'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Komunikasi / Komentar */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                <div className="w-7 h-7 bg-green-100 rounded-lg flex items-center justify-center">
                  <FontAwesomeIcon icon={faComments} className="w-3.5 h-3.5 text-green-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900 text-sm">Komunikasi</h2>
                  <p className="text-[11px] text-gray-400">Tanya atau sampaikan informasi tambahan kepada admin</p>
                </div>
              </div>

              {/* Message list */}
              <div className="p-4 space-y-3">
                {comments.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                    <FontAwesomeIcon icon={faComments} className="w-8 h-8 mb-2 opacity-30" />
                    <p className="text-sm">Belum ada pesan</p>
                  </div>
                ) : (
                  comments.map(c => {
                    const isUser = c.role === 'user'
                    return (
                      <div key={c.id} className={`flex gap-2.5 ${isUser ? 'justify-end' : 'justify-start'}`}>
                        {!isUser && (
                          <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <FontAwesomeIcon icon={faUser} className="w-3 h-3 text-blue-600" />
                          </div>
                        )}
                        <div className={`max-w-[75%] flex flex-col gap-1 ${isUser ? 'items-end' : 'items-start'}`}>
                          <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                            isUser
                              ? 'bg-green-600 text-white rounded-tr-sm'
                              : 'bg-gray-100 text-gray-800 rounded-tl-sm'
                          }`}>
                            {c.body}
                          </div>
                          {c.attachment && (
                            <button
                              type="button"
                              onClick={() => openFile(c.attachment)}
                              className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium transition-colors ${
                                isUser
                                  ? 'bg-green-500 border-green-400 text-white hover:bg-green-400'
                                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <FontAwesomeIcon icon={fileIcon(c.attachment.content_type)} className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="truncate max-w-[180px]">{c.attachment.name}</span>
                              <span className="opacity-60 flex-shrink-0">{formatBytes(c.attachment.size)}</span>
                              <FontAwesomeIcon icon={isPreviewable(c.attachment.content_type) ? faExpand : faDownload} className="w-3 h-3 flex-shrink-0" />
                            </button>
                          )}
                          <p className="text-[10px] text-gray-400 px-1">
                            {c.user_name} · {c.created_at}
                          </p>
                        </div>
                        {isUser && (
                          <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <FontAwesomeIcon icon={faUser} className="w-3 h-3 text-green-600" />
                          </div>
                        )}
                      </div>
                    )
                  })
                )}
                <div ref={commentsEndRef} />
              </div>

              {/* Input */}
              {communicationOpen ? (
                <form onSubmit={sendComment} className="px-4 pb-4 space-y-2">
                  {/* File request banner */}
                  {fileRequestOpen && evidenceFilesCount < 5 && (
                    <div className="flex items-center gap-2.5 bg-violet-50 border border-violet-200 rounded-xl px-3 py-2.5">
                      <FontAwesomeIcon icon={faFileArrowUp} className="w-4 h-4 text-violet-500 flex-shrink-0" />
                      <p className="text-xs text-violet-700 flex-1">Admin meminta Anda melampirkan file bukti dukung ({evidenceFilesCount}/5 file terkirim)</p>
                    </div>
                  )}
                  {fileRequestOpen && evidenceFilesCount >= 5 && (
                    <div className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5">
                      <FontAwesomeIcon icon={faFileCircleCheck} className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      <p className="text-xs text-emerald-700">Semua 5 file bukti dukung telah berhasil dikirim.</p>
                    </div>
                  )}
                  {/* File picker */}
                  {fileRequestOpen && evidenceFilesCount < 5 && (
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-2 px-3 py-2 bg-violet-50 border border-violet-200 rounded-xl cursor-pointer hover:bg-violet-100 transition-colors">
                        <FontAwesomeIcon icon={faFileArrowUp} className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
                        <span className="text-xs text-violet-700 flex-1">
                          Pilih file (maks. {5 - evidenceFilesCount} file, tiap file maks. 5MB)
                        </span>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={handleFileChange}
                          accept="image/*,application/pdf,video/*,.doc,.docx,.xls,.xlsx,.zip"
                        />
                      </label>
                      {selectedFiles.length > 0 && (
                        <div className="space-y-1">
                          {selectedFiles.map((file, idx) => (
                            <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-violet-50 border border-violet-100 rounded-lg">
                              <FontAwesomeIcon icon={fileIcon(file.type)} className={`w-3 h-3 flex-shrink-0 ${fileIconColor(file.type)}`} />
                              <span className="text-xs text-violet-800 flex-1 truncate">{file.name}</span>
                              <span className="text-[10px] text-gray-400 flex-shrink-0">{formatBytes(file.size)}</span>
                              <button
                                type="button"
                                onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== idx))}
                                className="w-5 h-5 flex items-center justify-center rounded hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                              >
                                <FontAwesomeIcon icon={faXmark} className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2 items-end">
                    <textarea
                      value={commentBody}
                      onChange={e => setCommentBody(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendComment(e) } }}
                      placeholder={selectedFiles.length > 0 ? 'Tambahkan keterangan untuk file (opsional)...' : 'Tulis pesan atau pertanyaan kepada admin...'}
                      rows={2}
                      className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2.5 text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none resize-none"
                    />
                    <button
                      type="submit"
                      disabled={(!commentBody.trim() && selectedFiles.length === 0) || sendingComment}
                      className="w-10 h-10 flex items-center justify-center bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors disabled:opacity-40 flex-shrink-0"
                    >
                      <FontAwesomeIcon icon={faPaperPlane} className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-400">Enter untuk kirim · Shift+Enter baris baru</p>
                </form>
              ) : (
                <div className="px-4 pb-4">
                  <div className="flex items-center gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                    <FontAwesomeIcon icon={faCommentDots} className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <p className="text-xs text-amber-700">Komunikasi belum dibuka oleh admin. Anda dapat membalas setelah admin mengirim pesan pertama.</p>
                  </div>
                </div>
              )}
            </div>
            </div>

            {/* Right column: Riwayat Aktivitas */}
            <div className="lg:col-span-3 space-y-4">
              <ActivityTimeline activities={activities} />
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
